// musicPlayer.js - Fixed transitions and better voice synthesis
export class MusicPlayer {
  constructor() {
    this.audioContext = null;
    this.currentSources = [];
    this.progressInterval = null;
    this.cleanupTimeout = null;
    this.isPlaying = false;
    this.playbackSpeed = 1.0;
    this.isUserInteraction = false;
    this.masterGain = null;
    this.currentPhrase = null;
    this.phraseStartTime = 0;
  }

  noteToFrequency(note) {
    const noteMap = {
      'E2': 82.41, 'F2': 87.31, 'F#2': 92.50, 'G2': 98.00, 'G#2': 103.83,
      'A2': 110.00, 'A#2': 116.54, 'B2': 123.47,
      'C3': 130.81, 'C#3': 138.59, 'D3': 146.83, 'D#3': 155.56, 'E3': 164.81,
      'F3': 174.61, 'F#3': 185.00, 'G3': 196.00, 'G#3': 207.65, 'A3': 220.00,
      'A#3': 233.08, 'B3': 246.94, 'C4': 261.63, 'D4': 293.66, 'E4': 329.63
    };
    return noteMap[note] || 130.81;
  }

  async ensureAudioContext() {
    if (!this.audioContext || this.audioContext.state === 'closed') {
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        this.audioContext = new AudioContext();
        this.audioContext.latencyHint = 'interactive';
        
        this.masterGain = this.audioContext.createGain();
        this.masterGain.gain.value = 1.5;
        this.masterGain.connect(this.audioContext.destination);
        
      } catch (error) {
        console.error('Audio context creation failed:', error);
        throw new Error('Audio not supported on this device');
      }
    }
    
    if (this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume();
        await new Promise(resolve => setTimeout(resolve, 50));
      } catch (resumeError) {
        console.error('Audio context resume failed:', resumeError);
        throw new Error('Audio playback requires user interaction');
      }
    }
    
    return this.audioContext;
  }

  async stopPlayback() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
    
    if (this.cleanupTimeout) {
      clearTimeout(this.cleanupTimeout);
      this.cleanupTimeout = null;
    }

    this.currentSources.forEach(source => {
      try {
        if (source && typeof source.stop === 'function') {
          source.stop();
        }
      } catch (e) {
        console.debug('Source stop failed:', e);
      }
    });
    this.currentSources = [];
    
    this.isPlaying = false;
    this.currentPhrase = null;
    return 0;
  }

  setPlaybackSpeed(speed) {
    this.playbackSpeed = Math.max(0.5, Math.min(2.0, speed));
  }

  setVolume(level) {
    if (this.masterGain) {
      const volume = Math.max(0, Math.min(3.0, level));
      this.masterGain.gain.value = volume;
    }
  }

  // IMPROVED: Clean note transitions without glissando
  createSpeechNote(fundamentalFreq, duration, context, startTime, isMaleVoice = true) {
    const sources = [];
    
    // Use triangle wave for smoother, less harsh sound
    const oscillator = context.createOscillator();
    const gainNode = context.createGain();
    oscillator.connect(gainNode);
    
    oscillator.type = 'triangle'; // Much smoother than sawtooth
    oscillator.frequency.value = fundamentalFreq;
    
    // Simple formant filtering for natural voice quality
    if (isMaleVoice) {
      const filter = context.createBiquadFilter();
      filter.type = 'lowshelf';
      filter.frequency.value = 400;
      filter.gain.value = 8.0; // Boost low frequencies for male voice
      gainNode.connect(filter);
      filter.connect(this.masterGain);
    } else {
      const filter = context.createBiquadFilter();
      filter.type = 'peaking';
      filter.frequency.value = 1200;
      filter.Q.value = 1.0;
      filter.gain.value = 6.0; // Boost mid frequencies for female voice
      gainNode.connect(filter);
      filter.connect(this.masterGain);
    }
    
    // Natural speech envelope - quick attack, gentle release
    const attack = Math.min(0.06, duration * 0.15);
    const decay = Math.min(0.08, duration * 0.2);
    const sustain = 0.7;
    const release = Math.min(0.12, duration * 0.3);
    
    const endTime = startTime + duration;
    
    // Higher base gain for better audibility
    let peakGain = isMaleVoice ? 0.8 : 0.7;
    
    gainNode.gain.setValueAtTime(0, startTime);
    gainNode.gain.linearRampToValueAtTime(peakGain, startTime + attack);
    gainNode.gain.linearRampToValueAtTime(peakGain * sustain, startTime + attack + decay);
    gainNode.gain.linearRampToValueAtTime(peakGain * sustain, endTime - release);
    gainNode.gain.linearRampToValueAtTime(0, endTime);
    
    oscillator.start(startTime);
    oscillator.stop(endTime + 0.1);
    sources.push(oscillator);
    
    // Add subtle harmonics for richness (especially for male voice)
    if (isMaleVoice && fundamentalFreq < 150) {
      const harmonic = context.createOscillator();
      const harmonicGain = context.createGain();
      harmonic.connect(harmonicGain);
      harmonicGain.connect(this.masterGain);
      
      harmonic.type = 'sine';
      harmonic.frequency.value = fundamentalFreq * 2; // Octave harmonic
      
      harmonicGain.gain.setValueAtTime(0, startTime);
      harmonicGain.gain.linearRampToValueAtTime(0.15, startTime + attack);
      harmonicGain.gain.linearRampToValueAtTime(0.1, startTime + attack + decay);
      harmonicGain.gain.linearRampToValueAtTime(0.1, endTime - release);
      harmonicGain.gain.linearRampToValueAtTime(0, endTime);
      
      harmonic.start(startTime);
      harmonic.stop(endTime + 0.1);
      sources.push(harmonic);
    }
    
    return sources;
  }

  // REMOVED: Glissando function - causing ugly transitions
  // Instead, we'll use clean note transitions with proper spacing

 async playNotes(notes, progressCallback) {
    this.isUserInteraction = true;
    const progress = await this.stopPlayback();
    await new Promise(resolve => setTimeout(resolve, 100));

    try {
      const audioContext = await this.ensureAudioContext();
      this.isPlaying = true;
      progressCallback(0);

      const sources = [];
      const noteEvents = [];
      let totalPlaybackTime = 0;

      console.log('Playing notes with pauses:', notes.filter(n => n.isPause).length, 'pauses detected');

      // Process notes with proper pause handling
      for (let i = 0; i < notes.length; i++) {
        const note = notes[i];
        
        if (note.isPause || note.isUnvoiced) {
          // Handle pauses - these are silent segments
          const pauseDuration = (note.duration || 0.3) / this.playbackSpeed;
          
          noteEvents.push({
            isPause: true,
            duration: pauseDuration,
            startTime: totalPlaybackTime,
            word: note.word,
            originalDuration: note.duration
          });
          
          totalPlaybackTime += pauseDuration;
          console.log(`Scheduled pause: ${pauseDuration.toFixed(2)}s at ${totalPlaybackTime.toFixed(2)}s`);
          
        } else if (note.musicalNote && note.duration) {
          // Handle voiced notes
          const frequency = this.noteToFrequency(note.musicalNote);
          const baseDuration = Math.max(0.15, note.duration);
          const playbackDuration = (baseDuration * 0.8) / this.playbackSpeed;
          
          // Add small gap between notes for cleaner transitions
          const gap = 0.02; // 20ms gap between notes
          
          noteEvents.push({
            frequency,
            duration: playbackDuration,
            startTime: totalPlaybackTime + (i > 0 ? gap : 0),
            isLocalMaxima: note.isLocalMaxima,
            isQuestion: note.isQuestion,
            word: note.word,
            index: i,
            isMaleVoice: frequency < 200
          });
          
          totalPlaybackTime += playbackDuration + (i > 0 ? gap : 0);
        }
      }

      const baseTime = audioContext.currentTime + 0.1;

      // Schedule audio events
      noteEvents.forEach((event) => {
        try {
          if (!event.isPause) {
            // Create speech note for voiced segments
            const voiceSources = this.createSpeechNote(
              event.frequency,
              event.duration,
              audioContext,
              baseTime + event.startTime,
              event.isMaleVoice
            );
            sources.push(...voiceSources);
          }
          // Pauses are silent - no audio source needed
        } catch (error) {
          console.warn('Failed to create audio source:', error);
        }
      });

      this.currentSources = sources;
      this.currentPhrase = notes;
      this.phraseStartTime = Date.now();

      console.log(`Total playback time: ${totalPlaybackTime.toFixed(2)}s including pauses`);

      // Progress tracking that respects pauses
      this.progressInterval = setInterval(() => {
        const elapsed = (Date.now() - this.phraseStartTime) / 1000;
        const progress = Math.min(100, (elapsed / totalPlaybackTime) * 100);
        progressCallback(progress);

        if (progress >= 100) {
          this.completePlayback(progressCallback);
        }
      }, this.isMobileDevice() ? 200 : 100);

      this.cleanupTimeout = setTimeout(() => {
        this.completePlayback(progressCallback);
      }, totalPlaybackTime * 1000 + 500);

    } catch (error) {
      console.error('Playback failed:', error);
      await this.stopPlayback();
      throw new Error(error.message.includes('user interaction') ? 
        'Please tap play again to start audio' : 'Playback failed');
    }
  }

  completePlayback(progressCallback) {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
    this.isPlaying = false;
    progressCallback(100);
  }

  getPlayerState() {
    return {
      isPlaying: this.isPlaying,
      playbackSpeed: this.playbackSpeed,
      currentPhrase: this.currentPhrase,
      canPlay: this.audioContext && this.audioContext.state !== 'closed'
    };
  }

  isMobileDevice() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (navigator.maxTouchPoints && navigator.maxTouchPoints > 2);
  }

  async quickPlay(notes, onProgress, onError) {
    try {
      await this.playNotes(notes, onProgress);
    } catch (error) {
      if (onError) onError(error.message);
    }
  }

  destroy() {
    this.stopPlayback().catch(console.debug);
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close().catch(console.debug);
    }
  }
}

export const musicPlayer = new MusicPlayer();